import { DocumentsFields, ProductEdited, ProductGraphicPreferenceStatus, ProductType } from './product';
import { BasicProgress } from './user-product';
import { GenericProgressStatus } from './progression';
import { ProductMenuEditionStatus } from './product-menu';
import { Palette } from './palette';
import { CustomImage } from './style';
import { BackgroundImages, Palettes } from './general-params';
import { User } from './user';
import { ToolsDocument } from './tools-document';
import { PaginationMeta } from './pagination';

export class ClassizEdited implements ProductEdited<ClassizModel> {
  slug: string;
  name: string;
  title?: string;
  visible?: boolean;
  active?: boolean;
  updatedAt?: Date;
  publishedAt?: Date;
  productType?: ProductType;
  data: ClassizModel;
  subType?: string;
  shareInProgress?: boolean;
  threadSlug?: string;
  demo?: boolean;
  folderProduct?: {
    slug: string;
  };
  hasConfigDraft?: boolean;

  constructor(classiz?: ClassizEdited) {
    this.slug = classiz?.slug;
    this.name = classiz?.name;
    this.title = classiz?.title;
    this.visible = classiz?.visible ?? false;
    this.active = classiz?.active ?? false;
    this.updatedAt = classiz?.updatedAt ? new Date(classiz.updatedAt) : null;
    this.publishedAt = classiz?.publishedAt ? new Date(classiz.publishedAt) : null;
    this.productType = classiz?.productType;
    this.subType = classiz?.subType;
    this.data = new ClassizModel(classiz?.data);
    this.shareInProgress = classiz?.shareInProgress ?? false;
    this.demo = classiz?.demo ?? false;
    this.folderProduct = classiz?.folderProduct;
    this.threadSlug = classiz?.threadSlug;
    this.hasConfigDraft = classiz?.hasConfigDraft;
  }
}

export class Classiz extends ClassizEdited {
  config: ClassizModel;
  configDraft: ClassizModel;
  constructor(classiz?: Classiz) {
    super(classiz);
    this.config = new ClassizModel(classiz?.config);
    this.configDraft = new ClassizModel(classiz?.configDraft);
  }
}

export class ClassizModel {
  slug: string;
  name: string;
  context: ClassizContext;
  params: ClassizParams;
  allowSelfValidation: boolean;
  klaxoonCode: string;
  editionInfo?: ClassizEditionInfo;
  documents?: DocumentsFields[];

  constructor(classiz?: ClassizModel) {
    this.slug = classiz?.slug;
    this.name = classiz?.name;
    this.context = new ClassizContext(classiz?.context);
    this.params = new ClassizParams(classiz?.params);
    this.allowSelfValidation = classiz?.allowSelfValidation ?? false;
    this.klaxoonCode = classiz?.klaxoonCode;
    this.editionInfo = new ClassizEditionInfo(classiz?.editionInfo);
    this.documents = classiz?.documents || [];
  }
}

export class ClassizContext {
  title: string;
  objectives: string;
  description: string;
  requirements: string;
  author: string;
  duration: string;

  constructor(context?: ClassizContext) {
    this.title = context?.title ?? null;
    this.objectives = context?.objectives ?? null;
    this.description = context?.description ?? null;
    this.requirements = context?.requirements ?? null;
    this.author = context?.author ?? null;
    this.duration = context?.duration ?? null;
  }
}

export class ClassizParams {
  status: ProductGraphicPreferenceStatus;
  colors: Palette;
  background: CustomImage;

  constructor(params?: ClassizParams) {
    this.colors = new Palette(params?.colors || Palettes[0]);
    this.background = new CustomImage(params?.background || BackgroundImages[0]);
    this.status = params?.status || ProductGraphicPreferenceStatus.DEFAULT;
  }
}

export class ClassizEditionInfo {
  paramsStatus: ProductMenuEditionStatus;
  contentStatus: ProductMenuEditionStatus;
  publicationStatus: ProductMenuEditionStatus;

  constructor(info?: ClassizEditionInfo) {
    this.paramsStatus = info?.paramsStatus ?? ProductMenuEditionStatus.PRISTINE;
    this.contentStatus = info?.contentStatus ?? ProductMenuEditionStatus.PRISTINE;
    this.publicationStatus = info?.publicationStatus ?? ProductMenuEditionStatus.PRISTINE;
  }
}

export interface ClassizResponse {
  product: Classiz;
  progress: ClassizProgress;
  frontDynamicPerimeterSlug: string;
}

export enum ClassizType {
  PRESENTIAL = 'presential',
  VIRTUAL = 'virtual',
}

export class ClassizProgress implements BasicProgress {
  percentage: number;
  status: GenericProgressStatus;
  time: number;
}

export type ClassizSlotShort = Pick<
  ClassizSlotDetail,
  'days' | 'description' | 'animators' | 'urls' | 'slug' | 'deleteInProgress'
>;
export type ClassizLearners = Pick<User, 'firstname' | 'lastname' | 'email' | 'identifier' | 'matriculeRh'> & {
  sign: ClassizSlotSignature[];
};
export interface ClassizSlotDetail {
  slug?: string;
  productName?: string;
  productType?: ClassizType;
  meanTarget?: string;
  days: ClassizSlotDays[];
  halfDays?: ClassizSlotDaysHalf[];
  description: string;
  updatedAt?: string; // atom date
  createdAt?: string; // atom date
  productDuration?: number;
  deleteInProgress?: boolean;
  options?: {
    authorizeMails: boolean;
  };
  animators: Pick<User, 'id' | 'firstname' | 'lastname' | 'email' | 'identifier' | 'matriculeRh'>[];
  learners?: ClassizLearners[];
  learnersExtras?: ClassizLearners[];
  documents?: Pick<ToolsDocument, 'slug' | 'name' | 'source'>[];
  urls: [
    {
      type: ClassizSlotUrlType;
      url: string;
    },
  ];
  signAt: string;
  signBy: Pick<User, 'id' | 'firstname' | 'lastname' | 'identifier'>;
}

export interface TargetsWithClassizs {
  slug: string;
  name: string;
  classizs: Classizs[];
}

export interface Classizs {
  slug: string;
  name: string;
  subType: string;
  duration: string;
  slot: {
    deleteInProgress: boolean;
    isModify: boolean; // if true, can not modified the slot
    slug: string;
    startAt: string; // atom date
  };
}

export interface UsersPaginate {
  learners?: {
    learners?: ClassizLearners[];
    learnersExtras?: ClassizLearners[];
  };
  meta: PaginationMeta;
}

export interface ClassizSlotDays {
  begin: string; // atom date
  end: string | null; // atom date
  uid: string | null;
  // TODO: à utiliser quand la pause déjeuner sera définie de façon explicite par l'utilisateur
  lunchBreak: boolean; // non utilisée pour le moment
}

export interface ClassizSlotDaysHalf {
  date: string;
  am: boolean;
  pm: boolean;
}

export enum ClassizSlotUrlType {
  KLAXOON = 'klaxoon',
}

export enum ClassizSignatureType {
  PRESENT = 'present',
  PARTIALLY = 'partially',
  ABSENT = 'absent',
}

export interface ClassizSignatureHalf {
  status: ClassizSignatureType;
  duration?: number | null;
  isRemove?: boolean; // Not for API
}

export interface ClassizSlotSignature {
  date?: string;
  am?: ClassizSignatureHalf;
  pm?: ClassizSignatureHalf;
}

export interface ClassizSlotLearnerSignatures {
  identifier: string;
  sign: ClassizSlotSignature[];
}

export interface ClassizSlotRescheduleAvailable {
  slotSlug: string;
  slotDays: string;
  meanTargetSlug: string;
}
